import './calendar-date.scss';

import {
  activeBookingStatuses,
  CalendarContext,
  checkIfWorkingDay,
  CommonContext,
  HolidayBooking,
  isBreakpointMobile,
  ModalActionKind,
  ReducerContext,
  SkeletonProps,
  translateStatusToClassName,
  useBreakpoint,
} from '@abdoo/common';
import { Skeleton } from '@mui/material';
import { format } from 'date-fns';
import moment from 'moment';
import React, { useCallback, useContext, useEffect, useState } from 'react';

import { CalendarDateDisplay } from './CalendarDateDisplay';

interface CalendarDateProps extends SkeletonProps {
  date: Date;
}

const HasActiveBooking = (holidayBooking: HolidayBooking) => {
  return activeBookingStatuses.includes(holidayBooking.approvalStatus);
};

const HasActiveBookings = (holidayBookings: HolidayBooking[]) => {
  return holidayBookings.some((h) => HasActiveBooking(h));
};

export function CalendarDate({ date, isLoading, skeletonSx }: CalendarDateProps) {
  const { freeDays } = useContext(CalendarContext);
  const { holidayState, modalDispatch, userSettingsState } = useContext(ReducerContext);
  const breakpoint = useBreakpoint();
  const isMobile = isBreakpointMobile(breakpoint);
  const [className, setClassName] = useState<string>('calendar_date');
  const [startMorning, setStartMorning] = useState(false);
  const [endAfternoon, setEndAfternoon] = useState(false);
  const [morningColorClass, setMorningColorClass] = useState('');
  const [afternoonColorClass, setAfternoonColorClass] = useState('');
  const [colorClass, setColorClass] = useState('');
  const { isOnline } = useContext(CommonContext);
  const onClick = useCallback(() => {
    const holidayBookingsInDay: HolidayBooking[] = holidayState.holidayBookings.filter(
      (em) =>
        format(new Date(em.startDate), 'yyyy/MM/dd') <=
          format(new Date(date), 'yyyy/MM/dd') &&
        format(new Date(em.endDate), 'yyyy/MM/dd') >=
          format(new Date(date), 'yyyy/MM/dd'),
    );
    if (HasActiveBookings(holidayBookingsInDay)) {
      modalDispatch({
        type: ModalActionKind.OpenBookingSummary,
        payload: holidayBookingsInDay,
      });
    } else {
      if (isOnline) {
        modalDispatch({
          type: ModalActionKind.OpenPromptAddAbsence,
          payload: moment(date),
        });
      }
    }
  }, [date, holidayState.holidayBookings, modalDispatch, isOnline]);

  useEffect(() => {
    setColorClass('');
    let classNameToSet = 'calendar_date';

    if (!checkIfWorkingDay(date, userSettingsState.workingDays)) {
      classNameToSet += ' calendar_date_weekend';
    }

    const datePart = format(date, 'yyyy-MM-dd');

    if (holidayState.holidays.map((x) => format(x, 'yyyy-MM-dd')).includes(datePart)) {
      const currentDate = new Date(date).setHours(0, 0, 0, 0);
      let upcoming = false;
      const bookings = holidayState.holidayBookings.filter(
        (x) =>
          new Date(x.startDate).setHours(0, 0, 0, 0) <= currentDate &&
          new Date(x.endDate).setHours(0, 0, 0, 0) >= currentDate,
      );
      bookings.forEach((x) => {
        if (HasActiveBooking(x) === false) {
          setMorningColorClass('');
          setAfternoonColorClass('');
          return;
        }
        upcoming =
          new Date(x.endDate).setHours(0, 0, 0, 0) > new Date().setHours(0, 0, 0, 0);
        const colorClassToSet = translateStatusToClassName(
          x.approvalStatus,
          upcoming,
          true,
        );
        if (
          x.isStartHalfDay &&
          currentDate === new Date(x.startDate).setHours(0, 0, 0, 0)
        ) {
          setMorningColorClass(colorClassToSet);
          setStartMorning(true);
          return;
        }
        if (x.isEndHalfDay && currentDate === new Date(x.endDate).setHours(0, 0, 0, 0)) {
          setAfternoonColorClass(colorClassToSet);
          setEndAfternoon(true);
          return;
        }
        setColorClass(colorClassToSet);
      });
    }

    if (freeDays && freeDays.find((em) => em === datePart)) {
      classNameToSet = classNameToSet + ' calendar_date_holiday';
    }
    setClassName(classNameToSet);
  }, [
    colorClass,
    date,
    freeDays,
    holidayState.holidayBookings,
    holidayState.holidays,
    userSettingsState.workingDays,
  ]);

  if (isLoading) {
    return (
      <Skeleton
        variant="rounded"
        sx={{
          width: { xs: '2.9%', md: '2.95%' },
          height: { md: '3.0rem' },
          marginLeft: '1px',
          ...skeletonSx,
        }}
      />
    );
  }
  return (
    <CalendarDateDisplay
      isMobile={isMobile}
      date={date}
      onClick={onClick}
      className={className}
      startMorning={startMorning}
      endAfternoon={endAfternoon}
      morningColorClass={morningColorClass}
      afternoonColorClass={afternoonColorClass}
      colorClass={colorClass}
    />
  );
}
